import { useState, useEffect } from "react";
import { getInsiderRisk } from "../api/InsiderRiskApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  AlertTriangle, AlertCircle, Shield, Clock,
  FileWarning, Lock, Activity, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

const InsiderRisk = () => {
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchData(filter, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, limit]);

  const fetchData = async (currentFilter, currentLimit) => {
    setLoading(true);
    try {
      const status = currentFilter === "all" ? null : currentFilter;
      const response = await getInsiderRisk(status, currentLimit);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load insider risk data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (f) => {
    setFilter(f);
    setLimit(50);
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case "critical": return { color: "#7f1d1d", bg: "bg-red-950/10", icon: AlertTriangle, label: "Critical" };
      case "high": return { color: "#ef4444", bg: "bg-red-50", icon: AlertCircle, label: "High" };
      case "medium": return { color: "#f59e0b", bg: "bg-amber-50", icon: Shield, label: "Medium" };
      case "low": return { color: "#1fa7a1", bg: "bg-teal-50", icon: Activity, label: "Low" };
      default: return { color: "#64748b", bg: "bg-gray-50", icon: Activity, label: "Unknown" };
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "data_exfiltration": return FileWarning;
      case "privilege_escalation": return Lock;
      case "anomalous_behavior": return Activity;
      case "policy_violation": return AlertCircle;
      default: return AlertTriangle;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const alerts = data?.alerts || [];

  // Initial page load — full-page Culsight loader
  if (loading && !data) {
    return <CulsightPageLoader variant="page" />;
  }

  const summary = data?.summary || {};

  return (
    <div
      className="relative space-y-6 animate-fadeIn"
      data-testid="insider-risk-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Insider Risk Indicators</h1>
        <p className="text-gray-500 text-sm mt-1">Monitor behavioral anomalies and security alerts</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Alerts", value: summary.total_alerts || 0, icon: AlertTriangle, color: "#8c3add", filterKey: "all" },
          { label: "Unresolved", value: summary.unresolved || 0, icon: Clock, color: "#f59e0b", filterKey: "unresolved" },
          { label: "Critical", value: summary.critical || 0, icon: AlertCircle, color: "#7f1d1d", filterKey: "critical" },
          { label: "High", value: summary.high || 0, icon: AlertTriangle, color: "#ef4444", filterKey: "high" },
          { label: "Medium/Low", value: summary.medium_low || 0, icon: Shield, color: "#1fa7a1", filterKey: "medium_low" }
        ].map((stat, index) => (
          <Card
            key={index}
            className={`border shadow-sm cursor-pointer transition-all hover:shadow-md ${filter === stat.filterKey ? "border-[#8c3add] ring-2 ring-[#8c3add]/20" : "border-gray-100"
              }`}
            onClick={() => handleFilterChange(stat.filterKey)}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon size={18} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1f1f23]">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alerts Timeline */}
      <Card className="border border-gray-100 shadow-sm relative" data-testid="alerts-card">
        {loading && (
          <div className="absolute inset-0 z-20 bg-white/70 backdrop-blur-[1px] rounded-xl">
            <div className="sticky top-24 flex items-center justify-center py-6">
              <CulsightPageLoader variant="overlay" />
            </div>
          </div>
        )}
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Recent Alerts</CardTitle>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "all", label: "All" },
              { key: "unresolved", label: "Unresolved" },
              { key: "critical", label: "Critical" },
              { key: "high", label: "High" },
              { key: "medium_low", label: "Medium/Low" },
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant={filter === key ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterChange(key)}
                className={filter === key ? "bg-[#8c3add] hover:bg-[#702eb0]" : ""}
              >
                {label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-50">
            {alerts.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                No alerts matching the filter
              </div>
            ) : (
              alerts.map((alert, index) => {
                const severity = getSeverityConfig(alert.severity);
                const AlertIcon = getAlertIcon(alert.alert_type);

                return (
                  <div
                    key={alert.id}
                    className={`p-5 hover:bg-gray-50/50 transition-colors ${!alert.resolved ? "border-l-4" : ""
                      }`}
                    style={{ borderLeftColor: !alert.resolved ? severity.color : "transparent" }}
                    data-testid={`alert-item-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className={`p-3 rounded-xl ${severity.bg}`}
                        style={{ color: severity.color }}
                      >
                        <AlertIcon size={22} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                className="font-medium"
                                style={{ backgroundColor: `${severity.color}15`, color: severity.color }}
                              >
                                {severity.label}
                              </Badge>
                              <Badge variant="outline" className="capitalize text-xs">
                                {alert.alert_type.replace("_", " ")}
                              </Badge>
                              {alert.resolved && (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle2 size={12} className="mr-1" />
                                  Resolved
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-semibold text-[#1f1f23] mt-2">
                              {alert.description}
                            </h4>
                          </div>
                          <span className="text-sm text-gray-400 whitespace-nowrap">
                            {formatTimeAgo(alert.timestamp)}
                          </span>
                        </div>

                        {/* User Info */}
                        <div className="flex items-center gap-3 mt-3 p-3 bg-gray-50 rounded-lg">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-[#8c3add]/10 text-[#8c3add] text-sm">
                              {alert.user_name.split(" ").map((n) => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-[#1f1f23]">{alert.user_name}</p>
                            <p className="text-xs text-gray-500">User ID: {alert.user_id}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {alerts.length >= limit && alerts.length < (summary.total_alerts || 0) && (
              <div className="p-4 text-center">
                <Button
                  variant="outline"
                  onClick={() => setLimit((prev) => prev + 50)}
                  disabled={loading}
                  className="gap-2"
                >
                  {loading && <CulsightPageLoader variant="inline" />}
                  {loading ? "Loading" : "Load More"}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alert Types Legend */}
      <Card className="border border-gray-100 shadow-sm">
        <CardContent className="p-6">
          <p className="text-sm font-medium text-gray-500 mb-4">Alert Type Reference</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { type: "data_exfiltration", label: "Data Exfiltration", desc: "Suspicious data transfers", icon: FileWarning, color: "#ef4444" },
              { type: "privilege_escalation", label: "Privilege Escalation", desc: "Unauthorized access attempts", icon: Lock, color: "#7f1d1d" },
              { type: "anomalous_behavior", label: "Anomalous Behavior", desc: "Unusual activity patterns", icon: Activity, color: "#f59e0b" },
              { type: "policy_violation", label: "Policy Violation", desc: "Security policy breaches", icon: AlertCircle, color: "#8c3add" }
            ].map((item, index) => (
              <div
                key={index}
                className="p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <item.icon size={18} style={{ color: item.color }} />
                  <span className="font-medium text-sm text-[#1f1f23]">{item.label}</span>
                </div>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InsiderRisk;